import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { Steps } from '../../../shared/components/steps/steps';
import { applyFormat, VivestControl } from '../../../core/directives/vivest-control';
import { emailValidator } from '../../../core/validators/email-validator';
import { cepValidator } from '../../../core/validators/cep-validator';
import { dddValidator } from '../../../core/validators/ddd-validator';
import { celularValidator } from '../../../core/validators/celular-validator';
import { BancoResponse, DadosCadastraisPayload, DadosCadastral, PaisResponse } from '../../../core/models/dados-cadastrais';
import { Router } from '@angular/router';
import { LoaderService } from '../../../shared/services/loader-service';
import { HttpClientService } from '../../../shared/services/http-client-service';
import type { VivestSelectOption } from '@vivest-dev/design-system-web-components';
import { ToastService } from '../../../shared/services/toast-service';

const MENSAGEM_OBRIGATORIO = 'O campo é obrigatório.';

/** Mensagens de obrigatoriedade específicas por campo; os demais usam MENSAGEM_OBRIGATORIO. */
const MENSAGENS_OBRIGATORIO_POR_CAMPO: Partial<Record<string, string>> = {
  nif: 'Informe o número de identificação fiscal (NIF) do país de residência.',
};

/** Mensagem por chave de erro retornada pelos validators, para campos preenchidos com valor inválido. */
const MENSAGENS_ERRO: Record<string, string> = {
  email: 'Informe um e-mail válido.',
  cep: 'Informe um CEP válido.',
  ddd: 'Informe um DDD válido.',
  telefone: 'Informe um número de telefone válido.',
  celular: 'Informe um número de celular válido.',
};

const MENSAGEM_CEP_FALHA = 'Não foi possível consultar o CEP. Tente novamente.';
const MENSAGEM_CEP_NAO_ENCONTRADO = 'CEP não encontrado. Preencha o endereço manualmente.';

/** Campos de endereço preenchidos pela consulta de CEP e desabilitados quando a API retorna valor. */
const CAMPOS_ENDERECO_CEP = ['uf', 'cidade', 'bairro', 'logradouro'] as const;

/** Campos exibidos e validados apenas quando o usuário reside no exterior. */
const CAMPOS_EXTERIOR = ['pais', 'zipCode', 'nif'] as const;

/**
 * Validações do endereço no Brasil (div #endereco). Quem reside no exterior não vê esses campos,
 * então as validações são retiradas; para quem reside no Brasil, ficam como no formulário.
 */
const VALIDADORES_ENDERECO = {
  cep: [Validators.required, cepValidator],
  uf: [Validators.required],
  cidade: [Validators.required],
  bairro: [Validators.required],
  logradouro: [Validators.required],
  numero: [Validators.required],
} satisfies Record<string, ValidatorFn[]>;

@Component({
  imports: [Steps, ReactiveFormsModule, VivestControl],
  selector: 'app-dados-cadastrais',
  styleUrl: './dados-cadastrais.scss',
  templateUrl: './dados-cadastrais.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DadosCadastrais {
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly loaderService = inject(LoaderService);
  private readonly httpClientService = inject(HttpClientService);
  private readonly toastService = inject(ToastService);

  readonly stepAtual = signal(3);
  readonly totalSteps = signal(7);
  readonly openModal = signal(true);
  readonly mostraDadosExterior = signal(false);
  /** Mensagem exibida no campo CEP quando a consulta falha ou o CEP não é encontrado. */
  readonly mensagemConsultaCep = signal('');

  protected readonly bancoOptions = signal<VivestSelectOption[]>([]);
  protected readonly paisOptions = signal<VivestSelectOption[]>([]);

  /** nome, cpf e dataNascimento vêm da API e não podem ser alterados, por isso são criados desabilitados. */
  protected readonly form = this.formBuilder.nonNullable.group({
    nome: [{ value: '', disabled: true }],
    cpf: [{ value: '', disabled: true }],
    email: ['', [Validators.required, emailValidator]],
    dataNascimento: [{ value: '', disabled: true }],
    emailSecundario: ['', [emailValidator]],
    dddCelular1: ['', [Validators.required, dddValidator]],
    celular1: ['', [Validators.required, celularValidator]],
    cep: ['', VALIDADORES_ENDERECO.cep],
    uf: ['', VALIDADORES_ENDERECO.uf],
    cidade: ['', VALIDADORES_ENDERECO.cidade],
    bairro: ['', VALIDADORES_ENDERECO.bairro],
    logradouro: ['', VALIDADORES_ENDERECO.logradouro],
    numero: ['', VALIDADORES_ENDERECO.numero],
    complemento: [''],
    banco: ['', [Validators.required]],
    agencia: ['', [Validators.required]],
    digitoAgencia: [''],
    conta: ['', [Validators.required]],
    digitoConta: [''],
    // Criados desabilitados (sem validação); habilitados apenas quando o usuário reside no exterior.
    pais: [{ value: '', disabled: true }, [Validators.required]],
    zipCode: [{ value: '', disabled: true }, [Validators.required]],
    nif: [{ value: '', disabled: true }, [Validators.required]],
  });

  constructor() {
    // Enquanto o usuário digita um novo CEP, esconde o erro anterior; ele volta a ser avaliado no blur.
    this.form.controls.cep.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.mensagemConsultaCep.set('');
      this.form.controls.cep.markAsUntouched();
    });

    // País, ZIP Code e NIF só são validados para quem reside no exterior; desabilitados, o Angular ignora a validação.
    effect(() => {
      const resideExterior = this.mostraDadosExterior();

      CAMPOS_EXTERIOR.forEach((campo) => {
        const control = this.form.controls[campo];

        if (resideExterior) {
          control.enable();
        } else {
          control.disable();
        }
      });

      // O endereço no Brasil fica oculto para quem reside no exterior, então deixa de ser obrigatório.
      // emitEvent: false para não disparar o valueChanges do CEP, que limparia a mensagem da consulta.
      (Object.keys(VALIDADORES_ENDERECO) as (keyof typeof VALIDADORES_ENDERECO)[]).forEach((campo) => {
        const control = this.form.controls[campo];
        const validadores = VALIDADORES_ENDERECO[campo];

        if (resideExterior) {
          control.removeValidators(validadores);
        } else {
          control.addValidators(validadores);
        }
        control.updateValueAndValidity({ emitEvent: false });
      });
    });
  }

  ngOnInit(): void {
    void this.carregarDadosCadastrais();
  }

  /** Busca os dados cadastrais e as listas de bancos e países, e preenche o formulário. */
  protected async carregarDadosCadastrais(): Promise<void> {
    this.loaderService.showLoader('Carregando...');

    try {
      const { body } = await this.httpClientService.getDadosCadastrais();
      if (!body) return;

      this.bancoOptions.set(body.bancos.map(toSelectOption));
      this.paisOptions.set(body.paises.map(toSelectOption));
      this.preencherFormulario(body.dadosCadastral, body.bancos);
    } catch {
      this.toastService.show('O sistema está indisponível. Tente novamente.', 'error');
    } finally {
      this.loaderService.hideLoader();
    }
  }

  /** Preenche o formulário com os dados da API, incluindo os campos desabilitados. */
  protected preencherFormulario(dados: DadosCadastral, bancos: BancoResponse[]): void {
    this.mostraDadosExterior.set(dados.residenteExterior === 'S');
    this.form.patchValue(paraFormulario(dados, bancos));
    // Mesmo comportamento da consulta de CEP: o que veio da API fica bloqueado, o que veio vazio fica para o usuário.
    this.desabilitarCamposEnderecoPreenchidos();
  }

  protected temErro(campo: keyof typeof this.form.controls): boolean {
    const control = this.form.controls[campo];
    return control.invalid && control.touched;
  }

  protected status(campo: keyof typeof this.form.controls): 'default' | 'error' {
    return this.temErro(campo) || this.temErroConsultaCep(campo) ? 'error' : 'default';
  }

  protected mensagemErro(campo: keyof typeof this.form.controls): string {
    if (!this.temErro(campo)) {
      return this.temErroConsultaCep(campo) ? this.mensagemConsultaCep() : '';
    }

    const erros = this.form.controls[campo].errors ?? {};

    if (erros['required']) {
      return MENSAGENS_OBRIGATORIO_POR_CAMPO[campo] ?? MENSAGEM_OBRIGATORIO;
    }

    const chave = Object.keys(erros).find((erro) => erro in MENSAGENS_ERRO);
    return chave ? MENSAGENS_ERRO[chave] : 'Verifique o valor informado.';
  }

  closeModal() {
    this.openModal.set(false);
  }

  onReturn() {
    this.router.navigate(['/instituto-online/escolha-instituto']);
  }

  /** Valida o formulário, salva os dados cadastrais na API e, com sucesso, segue para a modalidade de IR. */
  async onSubmit(): Promise<void> {
    // Marca todos os campos como tocados para exibir as mensagens de erro nos campos inválidos.
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    this.loaderService.showLoader('Salvando...');

    try {
      await this.httpClientService.saveDadosCadastrais(this.montarPayload());
      this.router.navigate(['/instituto-online/modalidade-ir']);
    } catch {
      this.toastService.show('Não foi possível salvar os dados cadastrais. Tente novamente.', 'error');
    } finally {
      this.loaderService.hideLoader();
    }
  }

  /**
   * Monta os dados no contrato de gravação da API (DadosCadastraisPayload). Usa getRawValue para
   * incluir os campos desabilitados (nome, data de nascimento, endereço preenchido pelo CEP).
   * Valores sem máscara e data no formato ISO. País, ZIP Code e NIF só vão para quem reside no exterior.
   */
  protected montarPayload(): DadosCadastraisPayload {
    const valores = this.form.getRawValue();
    const resideExterior = this.mostraDadosExterior();

    return {
      nome: valores.nome,
      dataNascimento: paraDataIso(valores.dataNascimento) ?? '',
      logradouro: valores.logradouro.trim(),
      numeroLogradouro: apenasDigitos(valores.numero),
      complemento: valores.complemento.trim(),
      bairro: valores.bairro.trim(),
      cidade: valores.cidade.trim(),
      uf: valores.uf.trim(),
      cep: apenasDigitos(valores.cep),
      dddCelular: paraNumero(valores.dddCelular1) ?? 0,
      celular: paraNumero(valores.celular1) ?? 0,
      emailPrincipal: valores.email.trim(),
      emailSecundario: valores.emailSecundario.trim(),
      codigoBanco: paraNumero(valores.banco) ?? 0,
      agencia: comDigito(apenasDigitos(valores.agencia), valores.digitoAgencia),
      contaCorrente: comDigito(apenasDigitos(valores.conta), valores.digitoConta),
      residenteExterior: resideExterior ? 'S' : 'N',
      pais: resideExterior ? rotuloDaOpcao(this.paisOptions(), valores.pais) : null,
      zipCode: resideExterior ? valores.zipCode : null,
      nif: resideExterior ? valores.nif : null,
    };
  }

  searchCep() {
    const cepControl = this.form.controls.cep;
    this.mensagemConsultaCep.set('');
    // Garante a exibição do erro de formato no blur, independentemente da ordem dos eventos blur/focusout.
    cepControl.markAsTouched();

    // Só consulta CEP válido; o erro de formato é exibido pelo validator.
    if(!cepControl.value || cepControl.invalid) {
      return;
    }

    this.loaderService.showLoader('Carregando...');
    this.habilitarCamposEndereco();

    this.httpClientService.getCep(cepControl.value)
    .then((response: any) => {
      // O ViaCEP responde 200 com { erro: true } quando o CEP não existe.
      if(!response || response.erro) {
        this.limparCamposEndereco();
        this.mensagemConsultaCep.set(MENSAGEM_CEP_NAO_ENCONTRADO);
      } else {
        this.form.patchValue({
          uf: response.uf ?? '',
          cidade: response.localidade ?? '',
          bairro: response.bairro ?? '',
          logradouro: response.logradouro ?? '',
          complemento: response.complemento ?? '',
        });
        this.desabilitarCamposEnderecoPreenchidos();
      }
      this.loaderService.hideLoader();
    })
    .catch((error) => {
      console.log(error);
      this.mensagemConsultaCep.set(MENSAGEM_CEP_FALHA);
      this.loaderService.hideLoader();
    });
  }

  private temErroConsultaCep(campo: keyof typeof this.form.controls): boolean {
    return campo === 'cep' && this.mensagemConsultaCep() !== '';
  }

  private habilitarCamposEndereco(): void {
    CAMPOS_ENDERECO_CEP.forEach((campo) => this.form.controls[campo].enable());
  }

  private limparCamposEndereco(): void {
    CAMPOS_ENDERECO_CEP.forEach((campo) => this.form.controls[campo].reset());
    this.form.controls.numero.reset();
    this.form.controls.complemento.reset();
  }

  /** Bloqueia a edição apenas dos campos que a API preencheu; os vazios ficam para o usuário completar. */
  private desabilitarCamposEnderecoPreenchidos(): void {
    CAMPOS_ENDERECO_CEP
      .filter((campo) => this.form.controls[campo].value)
      .forEach((campo) => this.form.controls[campo].disable());
  }
}

function toSelectOption({ id, descricao }: BancoResponse | PaisResponse): VivestSelectOption {
  return { value: String(id), label: descricao };
}

/** Converte a data ISO da API (1957-06-21T00:00:00) para o formato exibido (21/06/1957). */
function formatarDataIso(data: string | null): string {
  const [ano, mes, dia] = (data ?? '').split('T')[0].split('-');
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : '';
}

/** Converte a data exibida (21/06/1957) de volta para o formato ISO da API (1957-06-21T00:00:00). */
function paraDataIso(data: string): string | null {
  const [dia, mes, ano] = data.split('/');
  return ano && mes && dia ? `${ano}-${mes}-${dia}T00:00:00` : null;
}

/** Remove a máscara (pontos, traço, parênteses), mantendo os zeros à esquerda. */
function apenasDigitos(valor: string): string {
  return valor.replace(/\D/g, '');
}

/** Remove a máscara e envia como número; campo vazio vai como null. */
function paraNumero(valor: string): number | null {
  const digitos = apenasDigitos(valor);
  return digitos ? Number(digitos) : null;
}

/** Junta número e dígito da agência/conta separados por '-' (ex.: 123-4). Dígito não é obrigatório: vazio envia só o número. Mantém letras (ex.: 123-x). */
function comDigito(numero: string, digito: string): string {
  const digitoLimpo = digito.trim();
  return digitoLimpo ? `${numero}-${digitoLimpo}` : numero;
}

/** Nome exibido da opção selecionada (ex.: nome do país a partir do id); sem seleção, null. */
function rotuloDaOpcao(opcoes: VivestSelectOption[], valor: string): string | null {
  return opcoes.find((opcao) => opcao.value === valor)?.label ?? null;
}

/**
 * A API pode mandar o CEP sem os zeros à esquerda (5861300); completa com 0 até 8 dígitos
 * (05861300 → 05861-300) para não dar erro de CEP inválido. Sem CEP, o campo fica vazio para o usuário.
 */
function cepDaApi(cep: string | number | null): string {
  const digitos = String(cep ?? '').replace(/\D/g, '');
  return digitos ? applyFormat(digitos.padStart(8, '0'), 'cep') : '';
}

/**
 * O select usa o id do banco, mas a API manda só o nome: procura o nome na lista de bancos
 * (sem diferenciar maiúsculas). Sem nome ou fora da lista, o select fica vazio para o usuário escolher.
 */
function idDoBanco(nomeBanco: string | null, bancos: BancoResponse[]): string {
  const nome = (nomeBanco ?? '').trim().toUpperCase();
  const banco = bancos.find((item) => item.descricao.trim().toUpperCase() === nome);
  return banco ? String(banco.id) : '';
}

function paraFormulario(dados: DadosCadastral, bancos: BancoResponse[]) {
  const texto = (valor: string | number | null) => (valor === null || valor === undefined ? '' : String(valor));

  return {
    nome: texto(dados.nome),
    cpf: dados.cpf ? applyFormat(texto(dados.cpf).padStart(11, '0'), 'cpf') : '',
    dataNascimento: formatarDataIso(dados.dataNascimento),
    email: texto(dados.emailPrincipal),
    emailSecundario: texto(dados.emailPessoal),
    dddCelular1: applyFormat(texto(dados.dddCelular), 'ddd'),
    celular1: applyFormat(texto(dados.celular), 'celular'),
    cep: cepDaApi(dados.cep),
    uf: texto(dados.uf),
    cidade: texto(dados.cidade),
    bairro: texto(dados.bairro),
    logradouro: texto(dados.logradouro),
    numero: applyFormat(texto(dados.numeroLogradouro), 'digits'),
    complemento: texto(dados.complemento),
    banco: idDoBanco(dados.nomeBanco, bancos),
    agencia: applyFormat(texto(dados.agencia), 'digits'),
    digitoAgencia: texto(dados.digitoAgencia),
    conta: applyFormat(texto(dados.contaCorrente), 'digits'),
    digitoConta: applyFormat(texto(dados.digitoConta), 'digits'),
  };
}
